import React from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Container,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Slide,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CreateIcon from '@mui/icons-material/Create';
import HistoryIcon from '@mui/icons-material/History';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const getStatusIcon = (status, size = 18) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('rejected')) return <CancelIcon sx={{ fontSize: size, color: '#fff' }} />;
  if (s.includes('approved') || s === 'approved') return <CheckCircleIcon sx={{ fontSize: size, color: '#fff' }} />;
  if (s === 'created') return <CreateIcon sx={{ fontSize: size, color: '#fff' }} />;
  if (s.includes('pending')) return <HourglassEmptyIcon sx={{ fontSize: size, color: '#fff' }} />;
  return <CheckCircleIcon sx={{ fontSize: size, color: '#fff' }} />;
};

const getStatusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('rejected')) return 'error';
  if (s.includes('approved') || s === 'approved') return 'success';
  if (s === 'created') return 'info';
  if (s.includes('pending')) return 'warning';
  return 'default';
};

const getStatusConfig = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('rejected')) {
    return { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', dot: '#dc2626' };
  }
  if (s.includes('approved') || s === 'approved') {
    return { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', dot: '#16a34a' };
  }
  if (s === 'created') {
    return { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', dot: '#2563eb' };
  }
  if (s.includes('pending')) {
    return { bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#d97706' };
  }
  return { bg: '#f9fafb', border: '#e5e7eb', text: '#374151', dot: '#6b7280' };
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: '#1e293b',
  marginBottom: '1.25rem',
  paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const cancelBtnSx = {
  px: 3,
  py: 1,
  backgroundColor: '#f3f4f6',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontWeight: 500,
  textTransform: 'none',
  minWidth: '100px',
  height: '40px',
  '&:hover': { backgroundColor: '#e5e7eb' },
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const ApprovalHistoryModal = ({ open, asset, onClose }) => {
  if (!asset) return null;

  const history = Array.isArray(asset?.approvalHistory)
    ? [...asset.approvalHistory].sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      )
    : [];

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          margin: 0,
          boxSizing: 'border-box',
        },
      }}
    >
      {/* ── APP BAR ──────────────────────────────────────────────────────── */}
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              Approval History
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {asset.assetId} — {asset.assetName}
            </Typography>
          </Box>

          {history.length > 0 && (
            <Chip
              icon={<HistoryIcon sx={{ fontSize: '1rem', color: '#fff !important' }} />}
              label={`${history.length} ${history.length === 1 ? 'event' : 'events'}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontWeight: 600,
                '& .MuiChip-icon': { color: '#fff' },
              }}
            />
          )}
        </Toolbar>
      </AppBar>

      {/* ── SCROLLABLE BODY ───────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: '#f1f5f9',
          py: 4,
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={3}>

            {/* ── Timeline Card ─────────────────────────────────────────── */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Approval Timeline</SectionTitle>

                {/* Empty state */}
                {history.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>
                    <HourglassEmptyIcon
                      sx={{ fontSize: '3rem', opacity: 0.3, mb: 1 }}
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      No approval actions recorded yet
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                      Approval events will appear here once the workflow begins.
                    </Typography>
                  </Box>
                )}

                {/* Timeline */}
                {history.length > 0 && (
                  <Stack spacing={0}>
                    {history.map((entry, idx) => {
                      const isLast = idx === history.length - 1;
                      const level = Number(entry.level) || 0;
                      const status = entry.status || '—';
                      const config = getStatusConfig(status);
                      const isRejected = String(status)
                        .toLowerCase()
                        .includes('rejected');

                      return (
                        <Box
                          key={`${entry.createdAt}-${idx}`}
                          sx={{
                            display: 'flex',
                            gap: 2,
                            pb: isLast ? 0 : 3,
                            position: 'relative',
                          }}
                        >
                          {/* Timeline line + dot */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              flexShrink: 0,
                              width: 40,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: config.dot,
                                flexShrink: 0,
                                zIndex: 1,
                                boxShadow: `0 0 0 4px ${config.bg}`,
                              }}
                            >
                              {getStatusIcon(status, 20)}
                            </Avatar>
                            {!isLast && (
                              <Box
                                sx={{
                                  width: 2,
                                  flex: 1,
                                  bgcolor: '#e5e7eb',
                                  mt: 0.5,
                                  minHeight: 20,
                                }}
                              />
                            )}
                          </Box>

                          {/* Content */}
                          <Box
                            sx={{
                              flex: 1,
                              pt: 0.5,
                              pb: isLast ? 0 : 1,
                            }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                background: config.bg,
                                border: `1px solid ${config.border}`,
                              }}
                            >
                              {/* Title + status chip */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                  mb: 0.75,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={700}
                                  sx={{ color: '#1f2937' }}
                                >
                                  {level === 0
                                    ? 'Asset Created'
                                    : `Level ${level} Approval`}
                                </Typography>
                                <Chip
                                  label={status}
                                  size="small"
                                  color={getStatusColor(status)}
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 22,
                                  }}
                                />
                                {idx === history.length - 1 && (
                                  <Chip
                                    label="Latest"
                                    size="small"
                                    sx={{
                                      bgcolor: '#fef9c3',
                                      color: '#713f12',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      height: 22,
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Meta info */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  flexWrap: 'wrap',
                                  mb: entry.comment ? 1.5 : 0,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#6b7280' }}
                                >
                                  By:{' '}
                                  <strong style={{ color: '#374151' }}>
                                    {entry.createdByEmail || 'System'}
                                  </strong>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#6b7280' }}
                                >
                                  On:{' '}
                                  <strong style={{ color: '#374151' }}>
                                    {formatDateTime(entry.createdAt)}
                                  </strong>
                                </Typography>
                              </Box>

                              {/* Comment */}
                              {entry.comment && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 1.5,
                                    bgcolor: isRejected
                                      ? 'rgba(220, 38, 38, 0.05)'
                                      : 'rgba(0, 0, 0, 0.03)',
                                    border: `1px solid ${
                                      isRejected
                                        ? 'rgba(220, 38, 38, 0.15)'
                                        : 'rgba(0, 0, 0, 0.06)'
                                    }`,
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#6b7280',
                                      textTransform: 'uppercase',
                                      display: 'block',
                                      mb: 0.5,
                                      fontSize: '0.65rem',
                                      letterSpacing: '0.05em',
                                    }}
                                  >
                                    Comment
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.875rem',
                                      color: isRejected ? '#991b1b' : '#374151',
                                      fontStyle: 'italic',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    "{entry.comment}"
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </SectionCard>

          </Stack>
        </Container>
      </Box>

      {/* ── STICKY FOOTER ────────────────────────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          width: '100%',
          height: '72px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#fff',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.5,
          px: 3,
        }}
      >
        <Button onClick={onClose} sx={cancelBtnSx}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default ApprovalHistoryModal;